import { useCallback } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'

import { REDIRECT_FROM_APP } from '@local/utils'

import Clickable from '@web/components/Clickable'
import { useModal } from '@web/components/Modal/useModal'
import { gql } from '@web/gql'
import { UserGrade } from '@web/gql/graphql'
import { UseCheckinAccess_MeQuery } from '@web/hooks/useCheckinAccess/useCheckinAccess'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { useNavigation } from '@web/hooks/useNavigation'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import nClicks from '@web/libs/nlog/click/codes'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'
import { ClickHandler } from '@web/types'

import Alert from '../Alert'
import styles from './index.module.scss'
import { UnavailableType } from './userModal.constant'

const POLICY_LINK = 'https://m.place.naver.com/my/policy'

const WARNING_GRADE: Record<string, string> = {
  [UserGrade.Grey]: '1차',
  [UserGrade.GreySecond]: '2차',
  [UserGrade.GreyThird]: '3차',
  [UserGrade.Black]: '영구',
}

const getWarningClickCode = (isFromMapsApp: boolean) => {
  const nClicksArea = isFromMapsApp ? nClicks.gnbmap : nClicks.gnb
  return {
    [UserGrade.Grey]: {
      guide: nClicksArea.WARNINGGUIDE,
      close: nClicksArea.WARNINGCLOSE,
      policy: nClicksArea.WARNINGPOLICY,
    },
    [UserGrade.GreySecond]: {
      guide: nClicksArea.WARNING_2_GUIDE,
      close: nClicksArea.WARNING_2_CLOSE,
      policy: nClicksArea.WARNINGPOLICY,
    },
    [UserGrade.GreyThird]: {
      guide: nClicksArea.WARNING_3_GUIDE,
      close: nClicksArea.WARNING_3_CLOSE,
      policy: nClicksArea.WARNINGPOLICY,
    },
    [UserGrade.Black]: {
      guide: nClicksArea.BLACKGUIDE,
      close: nClicksArea.BLACKCLOSE,
      policy: nClicksArea.BLACKPOLICY,
    },
  } as { [key: string]: { guide: string; close: string; policy: string } }
}

export const Warning = ({
  unavailableAt,
  unavailableUntil,
  userGrade = UserGrade.Grey,
  isFromMapsApp = false,
  hasError = false,
}: {
  unavailableAt?: string
  unavailableUntil?: string
  userGrade?: UserGrade
  isFromMapsApp?: boolean
  hasError?: boolean
}) => {
  return (
    <div className={styles.content_area}>
      <div className={styles.tit_area}>{WARNING_GRADE[userGrade]} 이용 제한 안내</div>
      <div className={styles.txt_area}>
        플레이스 리뷰{' '}
        <Clickable
          className={styles.link}
          href={POLICY_LINK}
          newWindow
          nClickCode={getWarningClickCode(isFromMapsApp)[userGrade].policy}
        >
          이용정책
        </Clickable>
        에 맞지 않는
        <br />
        활동이 확인되었습니다.
        {unavailableUntil && (
          <>
            <br />
            이용 제한 기간 동안 모든 리뷰어 활동은 정지됩니다.
          </>
        )}
      </div>
      {!hasError && unavailableUntil && (
        <div className={classNames(styles.block_date_area)}>
          <span className={styles.txt}>
            이용제한 기간:{' '}
            <time aria-hidden="true">
              {dayjs(unavailableAt).locale('ko').format('YY/MM/DD')}~
              {dayjs(unavailableUntil).locale('ko').format('YY/MM/DD')}
            </time>
            <span className="place_blind">{dayjs(unavailableUntil).locale('ko').format('MM월 DD일 dddd 까지')}</span>
          </span>
        </div>
      )}
      {hasError && (
        <div className={styles.sub_txt_area}>
          이용제한 기간 업데이트 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.
        </div>
      )}
      {!hasError &&
        (userGrade !== UserGrade.Black ? (
          <div className={styles.sub_txt_area}>
            그동안 작성한 모든 리뷰가 확인되지 않을 수 있습니다. 또한 이용정책을 위반한 행위가 계속 발생하는 경우,
            리뷰어 활동 영구정지 및 MY플레이스로부터 취득한 포인트 전체 회수 조치될 수 있습니다.
            <div className={styles.warning_txt}>
              (위반 행위의 중대함에 따라 위 조치는
              <br />
              사전 통보 없이 이루어질 수 있습니다.)
            </div>
          </div>
        ) : (
          <div className={styles.sub_txt_area}>
            이에 따라 MY플레이스에서 취득한 포인트는 전체 회수되었으며 리뷰어 활동은 영구 정지되었습니다. (단, 예약/주문
            및 즐겨찾기 서비스는 정상적으로 이용 가능합니다)
          </div>
        ))}
    </div>
  )
}

export function useUnavailableUserModal(
  { isGoBack, shouldUseCheckinQuery }: { isGoBack?: boolean; shouldUseCheckinQuery?: boolean } = {
    isGoBack: false,
    shouldUseCheckinQuery: false,
  },
) {
  const router = useRouter()
  const { query } = router
  const redirectFromApp = query[`${REDIRECT_FROM_APP}`] === 'true'
  const navigation = useNavigation()

  const { data } = useQuery(
    gql(`
      query UseUnavailableUserModal_Me {
        ...UseUnavailableUserModal_Query
      }
    `),

    { skip: shouldUseCheckinQuery },
  )

  const { data: checkinData } = useQuery(UseCheckinAccess_MeQuery, { skip: !shouldUseCheckinQuery })

  const { MeSucceed } = extractByTypename(data?.me ?? checkinData?.me)
  const user = MeSucceed?.user

  const { isFromMapsApp } = useGlobalStaticStateContext()
  const [updateGreyPopupHistory] = useMutation(
    gql(`
      mutation updateGreyPopupHistory($clickType: String!) {
        updateGreyPopupHistory(clickType: $clickType)
      }
    `),
  )

  const userGrade = user?.currentGrade || UserGrade.Grey
  const unavailableUntil = user?.lastGradeHistory?.unavailableUntil ?? undefined

  const isBlackUser = userGrade === UserGrade.Black
  const handleCancelModal = () => {
    updateGreyPopupHistory({ variables: { clickType: 'close' } })

    if (isGoBack || redirectFromApp) {
      return navigation.back()
    }

    return null
  }

  const [updateGreyUserUnavailable] = useMutation(
    gql(`
      mutation updateGreyUserUnavailable {
        updateGreyUserUnavailable {
          ... on UpdateGreyUserUnavailableSucceed {
            user {
              currentGrade
              lastGradeHistory {
                unavailableUntil
                unavailableAt
              }
            }
          }
        }
      }
    `),
    {
      onCompleted: ({ updateGreyUserUnavailable }) => {
        const { UpdateGreyUserUnavailableSucceed, UpdateGreyUserUnavailableFailed, AuthenticationError } =
          extractByTypename(updateGreyUserUnavailable)
        const { currentGrade, lastGradeHistory } = UpdateGreyUserUnavailableSucceed?.user || {}
        /**
         * 정상 등급의 유저가 redirectFromApp=true를 붙이고 넘어온 경우
         * - 일반적인 상황에서 발생하지 않지만, 만일의 경우 대비해 정상 이용 가능하도록 방어코드 추가
         */
        if (currentGrade === UserGrade.Common) {
          return null
        }

        /**
         * @see https://oss.navercorp.com/place/local/pull/17846#discussion_r4135630
         */
        if (currentGrade !== UserGrade.Black && !lastGradeHistory?.unavailableUntil) {
          neloLogger(
            'warn',
            `어뷰저 팝업 오픈 시 값이 없는 경우: [objectId: ${user?.id}] [lastGradeHistory: ${JSON.stringify(
              lastGradeHistory,
            )}]  }`,
          )
        }
        return openModal({
          children: (
            <Warning
              userGrade={currentGrade as UserGrade}
              unavailableUntil={lastGradeHistory?.unavailableUntil ?? undefined}
              unavailableAt={lastGradeHistory?.unavailableAt ?? undefined}
              hasError={!!UpdateGreyUserUnavailableFailed || !!AuthenticationError}
            />
          ),
        })
      },
      onError: error => {
        neloLogger('error', `어뷰저 팝업 오픈 시 에러 발생: [objectId: ${user?.id}] ${extractErrorMessage(error)}`)
      },
    },
  )

  const handleConfirmModal: ClickHandler = () => {
    updateGreyPopupHistory({ variables: { clickType: 'guide' } })

    if (isBlackUser) {
      return navigation.push({ url: 'https://help.naver.com/alias/myplace/myplace_12.naver', openWindow: true })
    }
    return navigation.push({ url: 'https://help.naver.com/alias/myplace/myplace_11.naver', openWindow: true })
  }

  const { openModal } = useModal(Alert, {
    className: styles.modal_block,
    label: { confirm: '도움말 보기', cancel: '닫기' },
    callback: {
      confirm: handleConfirmModal,
      cancel: handleCancelModal,
    },
    clickCodes: {
      confirm: getWarningClickCode(isFromMapsApp)[userGrade]?.guide,
      cancel: getWarningClickCode(isFromMapsApp)[userGrade]?.close,
    },
    children: <Warning {...{ unavailableUntil, userGrade, isFromMapsApp }} />,
    autoCloseAfterConfirm: false,
  })

  const openUnavailableUserModal = useCallback(
    (type: UnavailableType) => {
      switch (type) {
        // 체크인 리뷰쓰기
        case UnavailableType.CHECKIN:
          return user?.isUnavailableCheckin && updateGreyUserUnavailable()
        // 체크인 외 리뷰쓰기
        case UnavailableType.REVIEW:
          return user?.isUnavailableReview && updateGreyUserUnavailable()
        // 리뷰쓰기 가능하나 징계기간이 지났지만 팝업을 보지 못해 최초 1회 팝업 노출대상
        case UnavailableType.ENTRY_CHECK:
          return (user?.isPenaltyEndWithoutNotice || redirectFromApp) && updateGreyUserUnavailable()
        default:
          return null
      }
    },
    [user],
  )

  return {
    openUnavailableUserModal,
    user,
  }
}

const fragment = gql(`
  fragment UseUnavailableUserModal_User on User {
    id
    isUnavailableCheckin
    isUnavailableReview
    isPenaltyEndWithoutNotice
    lastGradeHistory {
      unavailableUntil
    }
    currentGrade
  }

  fragment UseUnavailableUserModal_Query on Query {
    me {
      ... on MeSucceed {
        user {
          id
          ...UseUnavailableUserModal_User
        }
      }
      ... on BaseError {
        message
      }
    }
  }
`)
